import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { catalogApi } from "../api/client";

const FilterSideBar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [subcategories, setSubcategories] = useState([]);
  const [selectedSubIds, setSelectedSubIds] = useState([]);
  const [priceMin, setPriceMin] = useState("");
  const [priceMax, setPriceMax] = useState("");
  const [radiusKm, setRadiusKm] = useState(25);
  const [companyType, setCompanyType] = useState("");
  const [loading, setLoading] = useState(false);

  // Parse query params
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const categoryIds = useMemo(
    () =>
      (params.get("categoryIds") || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean),
    [params]
  );
  const categoryIdsKey = useMemo(
    () => params.get("categoryIds") || "",
    [location.search]
  );
  const hasLocation = useMemo(
    () => Boolean(params.get("lat") && params.get("lng")),
    [location.search]
  );

  // Initialize selected filters from URL
  useEffect(() => {
    const subsParam = params.get("subcategoryIds") || "";
    const priceMinParam = params.get("priceMin");
    const priceMaxParam = params.get("priceMax");
    const radiusParam = params.get("radiusKm");
    const companyTypeParam = params.get("companyType") || "";
    setSelectedSubIds(
      subsParam
        ? subsParam
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : []
    );
    setPriceMin(priceMinParam ?? "");
    setPriceMax(priceMaxParam ?? "");
    setRadiusKm(radiusParam ? Number(radiusParam) : 25);
    setCompanyType(companyTypeParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Fetch subcategories: if categories selected → fetch for those; else fetch all
  useEffect(() => {
    let isCancelled = false;
    (async () => {
      try {
        setLoading(true);
        let subcats = [];
        if (categoryIdsKey.length === 0) {
          const cats = await catalogApi.listCategories();
          const items = Array.isArray(cats?.items) ? cats.items : [];
          const all = await Promise.all(
            items.map((c) => catalogApi.listSubcategories(c._id))
          );
          subcats = (all || []).flatMap((r) => r?.items || []);
        } else {
          const all = await Promise.all(
            categoryIds.map((id) => catalogApi.listSubcategories(id))
          );
          subcats = (all || []).flatMap((r) => r?.items || []);
        }
        // Deduplicate by _id
        const seen = new Set();
        const unique = [];
        for (const s of subcats) {
          if (s && s._id && !seen.has(s._id)) {
            seen.add(s._id);
            unique.push(s);
          }
        }
        if (!isCancelled) setSubcategories(unique);
      } catch (_e) {
        if (!isCancelled) setSubcategories([]);
      } finally {
        if (!isCancelled) setLoading(false);
      }
    })();
    return () => {
      isCancelled = true;
    };
  }, [categoryIdsKey]);

  function toggleSubcategory(id) {
    setSelectedSubIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function applyFilters() {
    const next = new URLSearchParams(location.search);
    if (selectedSubIds.length > 0)
      next.set("subcategoryIds", selectedSubIds.join(","));
    else next.delete("subcategoryIds");

    if (priceMin !== "" && priceMin !== null)
      next.set("priceMin", String(Number(priceMin)));
    else next.delete("priceMin");

    if (priceMax !== "" && priceMax !== null)
      next.set("priceMax", String(Number(priceMax)));
    else next.delete("priceMax");

    if (hasLocation && radiusKm !== undefined && radiusKm !== null)
      next.set("radiusKm", String(Number(radiusKm)));
    else next.delete("radiusKm");

    if (companyType && companyType.length > 0)
      next.set("companyType", companyType);
    else next.delete("companyType");

    navigate({ pathname: location.pathname, search: next.toString() });
    if (onClose) onClose();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      });
    });
  }

  return (
    <div
      className={`fixed inset-y-0 left-0 z-100 w-72 bg-white shadow-2xl poppins-regular px-6 pb-5 border border-gray-200 space-y-4 transform transition-transform duration-300 md:static md:translate-x-0 md:w-11/12 md:min-w-80 md:rounded-2xl md:my-5 ${
        isOpen ? "translate-x-0" : "-translate-x-full"
      }`}
    >
      {/* Close button for mobile */}
      <div className="flex justify-between items-center lg:hidden">
        <h2 className="text-xl md:text-2xl font-bold text-gray-800 poppins-bold">
          Filters
        </h2>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-800 text-lg md:text-xl"
        >
          ✕
        </button>
      </div>

      {/* Header for desktop */}
      <h2 className="hidden lg:block text-xl md:text-2xl font-bold text-gray-800 poppins-bold">
        Filters
      </h2>

      {/* Company Type */}
      <div>
        <h3 className="text-sm md:text-base font-bold text-gray-700 mb-3 poppins">
          Company Type
        </h3>

        <select
          value={companyType}
          onChange={(e) => setCompanyType(e.target.value)}
          className="w-full p-2 border border-blue-300 rounded-lg text-xs md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All</option>
          <option value="manufacturer">Manufacturer</option>
          <option value="wholesaler">Wholesaler</option>
          <option value="distributor">Distributor</option>
          <option value="supplier">Supplier</option>
          <option value="industrial_services">Industrial Services</option>
          <option value="exporters">Exporters</option>
          <option value="traders">Traders</option>
          <option value="other">Other</option>
        </select>
      </div>

      {/* Subcategory Filter */}
      <div>
        <h3 className="text-sm md:text-base font-bold text-gray-700 mb-3 poppins">
          Sub categories{" "}
          {loading ? (
            <span className="text-xs text-gray-500">(Loading...)</span>
          ) : null}
        </h3>

        <ul className="space-y-3 text-gray-700 text-xs md:text-base max-h-72 overflow-auto pr-1">
          {(subcategories || []).map((s) => (
            <li key={s._id}>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  className="accent-blue-500 w-3 h-3 sm:w-4 sm:h-4"
                  checked={selectedSubIds.includes(s._id)}
                  onChange={() => toggleSubcategory(s._id)}
                />
                <span className="text-xs md:text-base">{s.name}</span>
              </label>
            </li>
          ))}

          {(!subcategories || subcategories.length === 0) && !loading && (
            <li className="text-xs md:text-sm text-gray-500">
              No subcategories
            </li>
          )}
        </ul>
      </div>

      {/* Price Range */}
      <div>
        <h3 className="text-sm md:text-base font-bold text-gray-700 mb-3 poppins-">
          Price Range
        </h3>
        <div className="flex items-center gap-4">
          <input
            type="number"
            placeholder="Min"
            value={priceMin}
            onChange={(e) => setPriceMin(e.target.value)}
            className="w-full p-2 border border-blue-300 rounded-lg text-xs md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          <input
            type="number"
            placeholder="Max"
            value={priceMax}
            onChange={(e) => setPriceMax(e.target.value)}
            className="w-full p-2 border border-blue-300 rounded-lg text-xs md:text-base focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Radius */}
      {hasLocation && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xs md:text-base font-bold text-gray-700 poppins uppercase">
              Radius (km)
            </h3>
            <span className="text-xs md:text-sm text-gray-600">
              {radiusKm} km
            </span>
          </div>

          <input
            type="range"
            min="1"
            max="100"
            step="1"
            value={radiusKm}
            onChange={(e) => setRadiusKm(Number(e.target.value))}
            className="w-full"
          />
        </div>
      )}

      {/* Apply Button */}
      <div>
        <button
          onClick={applyFilters}
          className="w-full py-3 bg-gradient-to-r from-blue-500 to-blue-700 text-white rounded-xl font-bold text-xs md:text-lg transition-all duration-300 shadow-md hover:shadow-lg hover:scale-[1.02]"
        >
          Apply Filters
        </button>
      </div>
    </div>
  );
};

export default FilterSideBar;
